import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import OktaAuth, { AuthState, UserClaims } from '@okta/okta-auth-js';
import { Observable, filter, map, of } from 'rxjs';
import { DemoDataService } from '@core/services/demo-data.service';

@Injectable({
  providedIn: 'root',
})
export class OktaIntegrationService {
  private _oktaAuth = inject(OKTA_AUTH);
  private _router = inject(Router);
  private _oktaAuthStateService = inject(OktaAuthStateService);
  private _demoDataService = inject(DemoDataService);
  private _demoUserLoggedIn = false;

  constructor() {}

  public getIsAuthenticated(): Observable<boolean> {
    if (this._demoDataService.isDemoActive()) {
      return of(this._demoUserLoggedIn);
    }
    return this._oktaAuthStateService.authState$.pipe(
      filter((s: AuthState) => !!s),
      map((s: AuthState) => s.isAuthenticated ?? false)
    );
  }

  /**
   * Function that will execute the redirection to the okta hosted page to proceed to the user identification.
   * @param {string} origin - page where the client who presses the `login` button is located
   * @param {string} navigateTo - page previous to redirect
   */
  public async signInWithRedirect(
    origin: string,
    navigateTo: string
  ): Promise<void> {
    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = true;
      return;
    }
    await this._oktaAuth
      .signInWithRedirect({ originalUri: origin })
      .then(_ => this._router.navigate([`${navigateTo}`]));
  }

  /**
   * Logout function, remove all information okta
   * @param {string} origin
   */
  public async signOut(origin: string): Promise<void> {
    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = false;
      return;
    }
    await this._oktaAuth.signOut();
  }

  /**
   * Return Patron info from Okta
   * @returns Promise<UserClaims>
   */
  public async getUserInfo(): Promise<UserClaims> {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return {
        sub: 'demo-user-123',
        email: 'demo@example.com',
        email_verified: true,
        name: 'Demo User',
        given_name: 'Demo',
        family_name: 'User',
        preferred_username: 'demo@example.com',
        accountCredit: 100_000,
      } as UserClaims;
    }
    return await this._oktaAuth.getUser();
  }

  public getAccessToken(): string {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return 'demo-access-token';
    }
    return this._oktaAuth.getAccessToken()!;
  }
}
